import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const MONTHLY_PAYMENT_CYCLE = 'MONTHLY'

export function guessTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return tz && typeof tz === 'string' && tz.length > 0 ? tz : 'UTC'
  } catch {
    return 'UTC'
  }
}

export function safeDayjsTz(tz = guessTimezone()) {
  try {
    const localDate = dayjs()
    if (!localDate.isValid() || !tz || typeof tz !== 'string') {
      return dayjs()
    }
    const tzDate = localDate.tz(tz)
    return tzDate.isValid() && isFinite(tzDate.toDate().getTime()) ? tzDate : dayjs()
  } catch {
    return dayjs()
  }
}

export function normalizeCycleStartDay(value) {
  const parsedValue = Number(value)
  if (!Number.isFinite(parsedValue)) {
    return 1
  }
  return Math.max(1, Math.min(Math.trunc(parsedValue), 31))
}

function getSafeCycleStartForMonth(monthMoment, cycleStartDay) {
  const monthStart = dayjs(monthMoment).startOf('month')
  return monthStart.date(Math.min(cycleStartDay, monthStart.daysInMonth())).startOf('day')
}

function resolveMonthlyCycleStart(referenceMoment, cycleStartDay) {
  const candidate = getSafeCycleStartForMonth(referenceMoment, cycleStartDay)
  if (dayjs(referenceMoment).isBefore(candidate, 'day')) {
    return getSafeCycleStartForMonth(dayjs(referenceMoment).subtract(1, 'month'), cycleStartDay)
  }
  return candidate
}

export function getPreviousPaymentCycleDateRange(
  paymentConfig,
  referenceMoment = safeDayjsTz().startOf('day'),
) {
  const safeReferenceMoment = dayjs(referenceMoment).isValid()
    ? dayjs(referenceMoment).startOf('day')
    : safeDayjsTz().startOf('day')
  const cycleStartDay = normalizeCycleStartDay(paymentConfig?.paymentCycleStartDayOfMonth)
  const paymentCycle = String(paymentConfig?.paymentCycle || MONTHLY_PAYMENT_CYCLE).toUpperCase()
  const cycleLengthInDays = Number(paymentConfig?.paymentCycleLengthInDays)
  const shouldUseFixedLengthCycle =
    paymentCycle !== MONTHLY_PAYMENT_CYCLE &&
    Number.isFinite(cycleLengthInDays) &&
    cycleLengthInDays > 0

  if (shouldUseFixedLengthCycle) {
    let currentCycleStart = resolveMonthlyCycleStart(safeReferenceMoment, cycleStartDay)
    while (!currentCycleStart.add(cycleLengthInDays, 'day').isAfter(safeReferenceMoment, 'day')) {
      currentCycleStart = currentCycleStart.add(cycleLengthInDays, 'day')
    }

    return {
      start: currentCycleStart.subtract(cycleLengthInDays, 'day').startOf('day'),
      end: currentCycleStart.subtract(1, 'day').startOf('day'),
    }
  }

  const currentCycleStart = resolveMonthlyCycleStart(safeReferenceMoment, cycleStartDay)
  const previousCycleEnd = currentCycleStart.subtract(1, 'day').startOf('day')

  return {
    start: resolveMonthlyCycleStart(previousCycleEnd, cycleStartDay).startOf('day'),
    end: previousCycleEnd,
  }
}

export function isCalendarMonthRange(startDate, endDate) {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  return (
    start.isValid() &&
    end.isValid() &&
    start.isSame(start.startOf('month'), 'day') &&
    end.isSame(start.endOf('month'), 'day')
  )
}

export function isPaymentCycleCalendarMonth(paymentConfig) {
  return isCalendarMonthRange(
    getPreviousPaymentCycleDateRange(paymentConfig).start,
    getPreviousPaymentCycleDateRange(paymentConfig).end,
  )
}

export function formatPaymentCycleLabel(startDate, endDate) {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  if (!start.isValid() || !end.isValid()) {
    return ''
  }
  if (isCalendarMonthRange(start, end)) {
    return start.format('MMM YYYY')
  }
  if (start.year() === end.year()) {
    return `${start.format('DMMM')}-${end.format('DMMM')}`
  }
  return `${start.format('DMMMYY')}-${end.format('DMMMYY')}`
}

export function getRangeKey(startDate, endDate) {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  return `${start.format('YYYY-MM-DD')}__${end.format('YYYY-MM-DD')}`
}

export function buildPaymentCycleOption(startDate, endDate, labelPrefix) {
  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).startOf('day')
  return {
    key: getRangeKey(start, end),
    label: labelPrefix || formatPaymentCycleLabel(start, end),
    start,
    end,
  }
}

export function getPaymentCycleShortcuts(paymentConfig, { count = 4, referenceMoment } = {}) {
  const shortcuts = []
  let cursor = referenceMoment ? dayjs(referenceMoment).startOf('day') : safeDayjsTz().startOf('day')

  for (let index = 0; index < count; index += 1) {
    const range = getPreviousPaymentCycleDateRange(paymentConfig, cursor)
    shortcuts.push(buildPaymentCycleOption(range.start, range.end))
    cursor = range.start.startOf('day')
  }

  return shortcuts
}
